import { DateQuestionModeType, RangeConditionType } from './types';

/**
 * How many offset fields a comparison takes.
 *
 * - `none`   the three predefined conditions, anchored to the moment of answering
 * - `single` one offset, read from `offsetHours`
 * - `dual`   two offsets, read from `offsetHoursToLeft` and `offsetHoursToRight`
 */
export type OffsetShape = 'none' | 'single' | 'dual';

/**
 * The one place that says what shape each comparison has. Every
 * `RangeConditionType` must have an entry here; an unlisted value silently
 * defaults to `single` via `UNKNOWN_CONDITION_SHAPE` below.
 */
const OFFSET_SHAPE_BY_CONDITION: Record<NonNullable<RangeConditionType>, OffsetShape> = {
  onlyToday: 'none',
  noFutureDates: 'none',
  noPastDates: 'none',
  after: 'single',
  afterOrEqual: 'single',
  before: 'single',
  beforeOrEqual: 'single',
  between: 'dual',
  notBetween: 'dual',
  // One reference plus an offset: the range runs from that answer to that
  // answer plus the offset.
  betweenQuestionOffset: 'single',
};

/** Default shape for a comparison not listed in `OFFSET_SHAPE_BY_CONDITION`. */
const UNKNOWN_CONDITION_SHAPE: OffsetShape = 'single';

export const getOffsetShape = (condition: RangeConditionType): OffsetShape => {
  if (!condition) return 'none';
  return OFFSET_SHAPE_BY_CONDITION[condition] ?? UNKNOWN_CONDITION_SHAPE;
};

/** True for the predefined conditions, which are anchored to the answering moment and take no offset. */
export const takesNoOffset = (condition: RangeConditionType): boolean =>
  getOffsetShape(condition) === 'none';

/** True for comparisons that read `offsetHoursToLeft` and `offsetHoursToRight`. */
export const takesTwoOffsets = (condition: RangeConditionType): boolean =>
  getOffsetShape(condition) === 'dual';

/** True for the one-sided comparisons whose reference is the upper bound rather than the lower one. */
export const isUpperBoundCondition = (condition: RangeConditionType): boolean =>
  condition === 'before' || condition === 'beforeOrEqual';

/**
 * The unit an offset is counted in, for a given question subtype: `date`
 * counts whole days, `datetime` and `time` count hours. Must agree with the
 * converters in `utils.ts`, which decide what the stored number means —
 * disagreeing produces a field labelled "Horas" that stores days.
 */
export type OffsetUnit = 'days' | 'hours';

export const getOffsetUnit = (dateMode: DateQuestionModeType | undefined): OffsetUnit =>
  dateMode === 'date' ? 'days' : 'hours';
